'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useFullscreenNav } from './fullscreen-nav';
import { useWorkspaceAnon } from './anon-context';
import { UPGRADE_REQUIRED_EVENT, type UpgradeRequiredDetail } from '@/lib/api/upgrade-required';

/**
 * The workspace's "you need a (real) account" dialog. Two triggers, both
 * surfaced as the fullscreen `/signup` dialog (`LoginDialogBody mode="signup"`,
 * which upgrades a guest in place when it's a guest session):
 *
 *   1. `requireSignup(...)` — an explicit "sign up" gesture (e.g. the welcome
 *      composer send). True-anon EDITS no longer call this: the first write
 *      mints a GUEST (`lib/api/fetch-with-guest-mint.ts`), so the gate is for
 *      deliberate signup CTAs only.
 *   2. `UPGRADE_REQUIRED_EVENT` — a guest hit an economy action (spend /
 *      publish / earn) and the server returned `403 upgrade_required`
 *      (`lib/auth/guest-gate.ts`). We open the same dialog so they can sign up
 *      free to keep their work and unlock it.
 *
 * Promise-dedupe keeps a single dialog open while many callers await it; a
 * dismissal latches so background callers stop re-popping it (an explicit
 * gesture clears the latch).
 */
type SignupReason = {
  reason?: string;
  /** Accepted for back-compat with callers; no longer stashed (guests replace
   * the clone-on-signup hand-off). */
  sourceWorkspaceId?: string | null;
  ref?: string | null;
  pendingPrompt?: string | null;
  /** A deliberate user gesture — always opens the dialog and clears the
   * dismissal latch. Background callers leave this falsey. */
  explicit?: boolean;
};

type SignupGateCtx = {
  requireSignup: (input?: SignupReason) => Promise<boolean>;
  enabled: boolean;
};

const Ctx = createContext<SignupGateCtx | null>(null);

export function SignupGateProvider({ children }: { children: React.ReactNode }) {
  const { anonReadOnly } = useWorkspaceAnon();
  const { openHref, isOpen } = useFullscreenNav();

  // Pending promise + resolver. While set, every incoming open returns the same
  // promise so a busy caller opens the dialog once.
  const pendingRef = useRef<{
    promise: Promise<boolean>;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const wasOpenRef = useRef(false);
  // Latched once the visitor dismisses the dialog so background callers stop
  // re-popping it. Cleared by an explicit gesture.
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current && pendingRef.current) {
      pendingRef.current.resolve(false);
      pendingRef.current = null;
      dismissedRef.current = true;
    }
    wasOpenRef.current = false;
  }, [isOpen]);

  const open = useCallback(
    (input?: SignupReason): Promise<boolean> => {
      if (pendingRef.current) return pendingRef.current.promise;
      if (input?.explicit) dismissedRef.current = false;
      else if (dismissedRef.current) return Promise.resolve(false);

      let resolveFn!: (ok: boolean) => void;
      const promise = new Promise<boolean>((resolve) => {
        resolveFn = resolve;
      });
      pendingRef.current = { promise, resolve: resolveFn };
      openHref('/signup');
      return promise;
    },
    [openHref],
  );

  const requireSignup = useCallback(
    (input?: SignupReason): Promise<boolean> => {
      // A guest (authed) never needs the gate for edits; only a true-anon, or
      // an explicit gesture, opens it. Everyone else resolves true.
      if (!anonReadOnly && !input?.explicit) return Promise.resolve(true);
      return open(input);
    },
    [anonReadOnly, open],
  );

  // Guest economy gate: a 403 upgrade_required anywhere on the page dispatches
  // this event (see lib/api/upgrade-required.ts) — open the upgrade dialog.
  useEffect(() => {
    const onUpgrade = (e: Event) => {
      const detail = (e as CustomEvent<UpgradeRequiredDetail>).detail;
      void open({ reason: detail?.reason, explicit: true });
    };
    window.addEventListener(UPGRADE_REQUIRED_EVENT, onUpgrade);
    return () => window.removeEventListener(UPGRADE_REQUIRED_EVENT, onUpgrade);
  }, [open]);

  const value = useMemo<SignupGateCtx>(
    () => ({ requireSignup, enabled: anonReadOnly }),
    [requireSignup, anonReadOnly],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSignupGate(): SignupGateCtx {
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  // Outside a workspace shell there's no gate — resolve as if already authed.
  // Server-side enforcement still gates economy actions regardless.
  return {
    requireSignup: () => Promise.resolve(true),
    enabled: false,
  };
}
